// MenschenFuersUnternehmenList.tsx — Firmenkarte „Verantwortung fürs
// Unternehmen" (ersetzt GfgList + VerantInlineList). Pro Eintrag: eine PERSON
// oder Firma (suchen/eintippen) + die VerantwortlichkeitInput-Komponente
// (intern/extern, GF/Gesellschafter + Katalog-Verantwortlichkeiten).
// Kein Speichern hier — nur Sammeln im Form-State (`rel_menschen`).
//
// Seit 28.09.2026 in getrennten Listen (menschenGruppen.ts), im Aussehen der
// Verknüpfungen der Kontaktakte (BeziehungsTabelle):
//   Unternehmensleitung und Gesellschafter (EINE Liste) · Verantwortung fürs
//   Unternehmen · Mensch im Unternehmen bzw. im Verein (eingeklappt)
// Solange alles leer ist, stehen nur die beiden Knöpfe „+ Verantwortung fürs
// Unternehmen" und „+ Mensch im Unternehmen" da — eine Liste erscheint erst
// mit ihrem ersten Eintrag. Angelegt wird über EIN Popup; beim Bearbeiten
// fragt es zusätzlich „Person ist ausgeschieden?".
//
// In der Akte einer Firma (seit 30.09.2026, AkteSofortSpeichernContext):
//   - Name → Akte der Person (wie bisher), Rolle → Popup „Verantwortung fürs
//     Unternehmen" mit „Daten ändern" (VerantwortungDatenDialog), auch bei
//     geschlossenem Schloss, sofern Schreibrecht besteht.
//   - Stift in „Mensch im Unternehmen" → kein Popup, sondern Inline-Editing:
//     geschäftliches Telefon und geschäftliche E-Mail zu DIESER Firma, sofort
//     gespeichert. In der gesperrten Maske (dort sind alle Felder aus) öffnet
//     der Stift stattdessen gleich „Daten ändern".
// Das gilt nur für gespeicherte Personen; alles andere bleibt beim Sammeln im
// Formular (Anlage, neu hinzugefügte Einträge, Firmen als Gesellschafterin).

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { EintragDialog } from "../forms/EintragDialog";
import {
  AnsichtsKnopf,
  BeziehungsTabelle,
  HinzufuegenKnopf,
  type BeziehungsZeile,
  type TabellenAblage,
} from "../forms/BeziehungsTabelle";
import { ZIEH_ART_FIRMENAKTE, ziehDatenVon, ziehMarkerVon, type FirmenListe, type ZiehQuelle } from "./beziehungsZiehen";
import { useZiehtGerade } from "./useZiehtGerade";
import { refName, zeilenAnzeigeVon } from "./beziehungsAnzeige";
import { ContactRefField } from "./ContactRefField";
import { useRelStyles } from "./styles";
import { uid } from "./types";
import type { AusgeschiedenFelder as AusgeschiedenWerte, ContactRef } from "./types";
import {
  VerantwortlichkeitInput,
  type VerantwortlichkeitValue,
} from "../verantwortlichkeit/VerantwortlichkeitInput";
import { useVerantwortlichkeiten } from "../verantwortlichkeit/useVerantwortlichkeiten";
import {
  verantwortungTitel,
  imDemUnternehmen,
  leitungTitel,
  loadLegalFormOptionsCached,
} from "../../utils/unternehmensBegriff";
import { istVerwaltungsgesellschaftsEintrag, rechtsformProfil } from "../../utils/komplementaerGmbh";
import {
  baueKategorieVon,
  gruppenAnteile,
  gruppenRolleText,
  gruppenVon,
  ohneGruppen,
  type MenschGruppe,
} from "./menschenGruppen";
import { AusgeschiedenFelder, ausgeschiedenText } from "./AusgeschiedenFelder";
import { useMitarbeiterAbsicherung, menschenZusatzSpalten, type InlineBearbeitung } from "./menschenZusatzSpalten";
import {
  UNGESPEICHERT_HINWEIS,
  useAkteSofortSpeichern,
  useAnsichtsZustand,
} from "./akteSofortSpeichern";
import { VerantwortungDatenDialog } from "./VerantwortungDatenDialog";
import {
  firmenKommunikationSpeichern,
  sofortFehlerText,
  type FirmenKommunikationTyp,
} from "../../services/firmenKommunikationService";

export type MenschEntry = AusgeschiedenWerte & {
  id: string;
  ref: ContactRef | null;
  verant: VerantwortlichkeitValue | null;
  /**
   * Beim Umwandeln: die bestehenden Beziehungs-Zeilen dieser Person zur Firma.
   * Sie gehören dem `attach-menschen`-Endpoint, der sie transaktional ersetzt,
   * und dürfen deshalb NICHT in den Lösch-Schritt des Abgleichs geraten.
   */
  relationIds?: number[];
};

const emptyVerant = (): VerantwortlichkeitValue => ({ isExtern: false, roles: [], externalCompany: null });

/**
 * Popup von außen öffnen: „+ Verbindung zu Unternehmen" → „Verantwortung fürs
 * Unternehmen (extern)" (Paket A5) und Ziehen & Ablegen (Paket A6).
 */
export type MenschenSteuerung = {
  neu: (opts?: {
    /** Gegenüber ist eine Firma (Suche nur nach Firmen), z. B. der Steuerberater. */
    nurFirma?: boolean;
    /** Vorbelegung des Eintrags (Gegenüber, Rollen …). */
    vorlage?: Partial<MenschEntry>;
  }) => void;
  /**
   * Popup für einen BESTEHENDEN Eintrag öffnen (Ziehen & Ablegen, A6);
   * `haken` setzt dabei „Mensch im Unternehmen". Abbrechen ändert nichts.
   */
  bearbeiten: (id: string, opts?: { haken?: boolean }) => void;
};

/** Welche Liste der Karte zu welcher Zieh-Liste gehört. */
const ZIEH_LISTE: Record<"leitungGesellschafter" | "verantwortung" | "mensch", FirmenListe> = {
  leitungGesellschafter: "leitung",
  verantwortung: "verantwortung",
  mensch: "mensch",
};

/** Die drei Listen der Karte — Leitung und Gesellschafter stehen zusammen. */
const LISTEN = {
  leitungGesellschafter: ["leitung", "gesellschafter"] as MenschGruppe[],
  verantwortung: ["verantwortung"] as MenschGruppe[],
  mensch: ["mensch"] as MenschGruppe[],
};
type ListenArt = keyof typeof LISTEN;

const useStyles = makeStyles({
  wrap: { display: "flex", flexDirection: "column", rowGap: "26px" },
  fuss: { display: "flex", justifyContent: "flex-end", flexWrap: "wrap", columnGap: "10px", rowGap: "8px" },
  klappKopf: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    alignSelf: "flex-start",
    cursor: "pointer",
    fontFamily: REFINED.fontUI,
    fontWeight: 700,
    fontSize: "16px",
    color: REFINED.ink,
    ...shorthands.borderRadius("8px"),
    ":focus-visible": { outlineStyle: "solid", outlineWidth: "2px", outlineColor: REFINED.aub },
  },
  anzahl: { fontWeight: 600, color: REFINED.ink3 },
  hinweis: {
    fontFamily: REFINED.fontUI,
    fontSize: "13px",
    color: REFINED.err,
    backgroundColor: REFINED.errBg,
    ...shorthands.padding("8px", "12px"),
    ...shorthands.borderRadius("9px"),
  },
  chevron: { transitionProperty: "transform", transitionDuration: "150ms", color: REFINED.ink3 },
  chevronOffen: { transform: "rotate(90deg)" },
  klapp: { display: "flex", flexDirection: "column", rowGap: "12px" },
});

export function MenschenFuersUnternehmenList({
  value,
  onChange,
  begriff,
  legalFormId,
  gesellschafterAusVerbindungen = [],
  zentrale,
  firmaId,
  strukturFirmenIds,
  steuerung,
  ziehen,
}: {
  value: MenschEntry[];
  onChange: (next: MenschEntry[]) => void;
  /** Begriff aus der Rechtsform („Unternehmen", „Verein" …) für die Beschriftung. */
  begriff?: string;
  /**
   * Rechtsform der Firma, die gerade angelegt/bearbeitet wird. Sie bestimmt, welche
   * Organrollen wählbar sind — nicht die Rechtsform des Gegenkontakts.
   */
  legalFormId?: number;
  /**
   * Firmen, die über „Verbindungen zu Firmen" als Gesellschafterin/Mutter
   * erfasst sind — erscheinen zur Übersicht auch unter „Unternehmensleitung
   * und Gesellschafter" (nur lesend; gepflegt werden sie in ihrer Karte).
   */
  gesellschafterAusVerbindungen?: BeziehungsZeile[];
  /** Zentralnummer(n) der Firma — Durchwahl der geschäftlichen Nummer neu erfasster Personen. */
  zentrale?: readonly (string | null | undefined)[];
  /**
   * Id der Firma, wenn sie schon gespeichert ist (Akte). Dann zeigt „Mensch im
   * Unternehmen" wie früher Abteilung und bAV/bKV/UV/Betriebshaftpflicht.
   */
  firmaId?: number;
  /**
   * Firmen, mit denen diese Firma über eine Strukturkante verbunden ist
   * („Verbindungen zu Firmen"). Eine Komplementärin, die dort zugleich steht,
   * ist Holding und bleibt sichtbar (T11).
   */
  strukturFirmenIds?: ReadonlySet<number>;
  /** Popup von außen öffnen (siehe {@link MenschenSteuerung}). */
  steuerung?: React.MutableRefObject<MenschenSteuerung | null>;
  /**
   * Ziehen & Ablegen zwischen den Listen der Firmenakte (Paket A6): `ablage`
   * je Ziel-Liste, `quelle` beschreibt eine gezogene Zeile.
   */
  ziehen?: {
    ablage: (ziel: FirmenListe) => TabellenAblage;
    quelle: (e: MenschEntry, liste: FirmenListe) => ZiehQuelle;
  };
}) {
  const s = useStyles();
  const rs = useRelStyles();
  const { items: katalog } = useVerantwortlichkeiten();
  const kategorieVon = React.useMemo(() => baueKategorieVon(katalog), [katalog]);
  const [offen, setOffen] = React.useState<{
    id: string | null;
    entwurf: MenschEntry;
    nurAnsehen?: boolean;
    /** Gegenüber ist eine Firma — Suche nur nach Firmen (A5, extern). */
    nurFirma?: boolean;
  } | null>(null);
  const abbrechen = React.useCallback(() => setOffen(null), []);
  const sofort = useAkteSofortSpeichern();
  // „Mensch im Unternehmen" ist wie in der Akte zugeklappt (User 28.09.2026).
  // Aufgeklappt und die inline bearbeitete Person überleben das Neuladen nach
  // dem Sofort-Speichern (akteSofortSpeichern.ts).
  const [menschOffen, setMenschOffen] = useAnsichtsZustand(sofort, `menschOffen-${firmaId ?? ""}`, false);
  const [inlinePerson, setInlinePerson] = useAnsichtsZustand<number | null>(sofort, `inline-${firmaId ?? ""}`, null);
  const [datenDialog, setDatenDialog] = React.useState<{ eintragId: string; modus: "ansehen" | "aendern" } | null>(
    null,
  );
  const [hinweis, setHinweis] = React.useState<string | null>(null);

  /** Darf dieser Eintrag sofort gespeichert werden (Akte, gespeicherte Person)? */
  const sofortFuer = (e: MenschEntry): boolean =>
    !!sofort && !!firmaId && !!e.ref?.contactId && e.ref.type !== "Firma" && !!e.relationIds?.length;

  // Rollenprofil der Rechtsform — bei einer GmbH & Co. KG wird die
  // Komplementär-GmbH (reine Verwaltungsgesellschaft) nicht gezeigt (T11). Sie
  // bleibt im Formularzustand und wird beim Speichern nicht angefasst.
  const kgProfil = useRollenProfil(legalFormId);
  const ausgeblendet = (e: MenschEntry) =>
    istVerwaltungsgesellschaftsEintrag(e, kgProfil, { firmaId, strukturFirmenIds });

  const menschTitel = `Mensch ${imDemUnternehmen(begriff)}`;
  const verantTitel = verantwortungTitel(begriff);

  const zeilenFuer = (art: ListenArt): BeziehungsZeile[] => {
    const gruppen = LISTEN[art];
    return value
      .filter((e) => !ausgeblendet(e))
      .filter((e) => {
        const g = gruppenVon(e, kategorieVon);
        // Ohne jede Rolle (etwa nach dem Umwandeln) wäre der Eintrag sonst
        // unsichtbar — er steht dann unter „Verantwortung" und wartet auf eine.
        return gruppen.some((x) => g.has(x)) || (art === "verantwortung" && g.size === 0);
      })
      .map((e) => ({
        ...(ziehen && e.ref
          ? (() => {
              const q = ziehen.quelle(e, ZIEH_LISTE[art]);
              return { ziehDaten: ziehDatenVon(q), ziehMarker: ziehMarkerVon(q) };
            })()
          : {}),
        key: `${e.id}-${art}`,
        name: refName(e.ref) || "Ohne Namen",
        rolle: gruppenRolleText(e, gruppen, kategorieVon, menschTitel, { ohneAnteil: true }) || undefined,
        anteile: gruppenAnteile(e, gruppen, kategorieVon) || undefined,
        ...zeilenAnzeigeVon(e.ref),
        hinweis: ausgeschiedenText(e) || undefined,
        durchgestrichen: !!e.ausgeschieden,
        onBearbeiten: ({ nurAnsehen }) => {
          if (art !== "mensch" || !sofortFuer(e)) return setOffen({ id: e.id, entwurf: e, nurAnsehen });
          // Gesperrte Maske: Die Felder der Zeile wären aus — gleich „Daten ändern".
          if (nurAnsehen) return setDatenDialog({ eintragId: e.id, modus: "aendern" });
          if (inlinePerson === e.ref!.contactId) return setInlinePerson(null);
          if (sofort!.hatUngespeicherte()) return setHinweis(UNGESPEICHERT_HINWEIS);
          setHinweis(null);
          setInlinePerson(e.ref!.contactId!);
        },
        onRolle: ({ nurAnsehen }) =>
          sofortFuer(e)
            ? setDatenDialog({ eintragId: e.id, modus: "ansehen" })
            : setOffen({ id: e.id, entwurf: e, nurAnsehen }),
        inlineAktiv: art === "mensch" && sofortFuer(e) && inlinePerson === e.ref?.contactId,
        onEntfernen: () => {
          const rest = ohneGruppen(e, gruppen, kategorieVon);
          onChange(rest ? value.map((x) => (x.id === e.id ? rest : x)) : value.filter((x) => x.id !== e.id));
        },
      }));
  };

  const neu = (menschImUnternehmen = false) =>
    setOffen({
      id: null,
      entwurf: {
        id: uid(),
        ref: null,
        verant: menschImUnternehmen
          ? { ...emptyVerant(), istMenschImUnternehmen: true, istMenschImUnternehmenManuell: true }
          : emptyVerant(),
      },
    });

  // Aktueller Stand für die Steuerung von außen (sie lebt über Renderings hinweg).
  const valueRef = React.useRef(value);
  valueRef.current = value;

  React.useEffect(() => {
    if (!steuerung) return;
    steuerung.current = {
      bearbeiten: (id, opts) => {
        const e = valueRef.current.find((x) => x.id === id);
        if (!e) return;
        setOffen({
          id: e.id,
          entwurf: opts?.haken
            ? {
                ...e,
                verant: {
                  ...(e.verant ?? emptyVerant()),
                  istMenschImUnternehmen: true,
                  istMenschImUnternehmenManuell: true,
                },
              }
            : e,
        });
      },
      neu: (opts) =>
        setOffen({
          id: null,
          nurFirma: opts?.nurFirma,
          entwurf: {
            id: uid(),
            ref: null,
            ...(opts?.vorlage ?? {}),
            verant: opts?.vorlage?.verant ?? (opts?.nurFirma ? { ...emptyVerant(), isExtern: true } : emptyVerant()),
          },
        }),
    };
    return () => {
      steuerung.current = null;
    };
  }, [steuerung]);

  const uebernehmen = () => {
    if (!offen?.entwurf.ref || offen.nurAnsehen) return;
    const e = offen.entwurf;
    onChange(offen.id === null ? [...value, e] : value.map((x) => (x.id === offen.id ? e : x)));
    setOffen(null);
  };
  const update = (patch: Partial<MenschEntry>) =>
    setOffen((o) => (o ? { ...o, entwurf: { ...o.entwurf, ...patch } } : o));

  const leitungGesellschafter = [...zeilenFuer("leitungGesellschafter"), ...gesellschafterAusVerbindungen];
  const verantwortung = zeilenFuer("verantwortung");
  const menschen = zeilenFuer("mensch");
  const spalten = { rolle: "Rolle", anteile: "Anteile" };
  // Anteile gibt es nur bei Leitung und Gesellschaftern (User 28.09.2026).
  const spaltenOhneAnteile = { rolle: "Rolle" };
  // „Mensch im Unternehmen" mit den Spalten der früheren Akte: Telefon, E-Mail
  // und — sobald die Firma gespeichert ist — Abteilung und die Absicherungen.
  const absicherung = useMitarbeiterAbsicherung(firmaId, (menschOffen && menschen.length > 0) || !!datenDialog);

  // Inline: geschäftliches Telefon / E-Mail zu DIESER Firma, beim Verlassen
  // sofort gespeichert, danach lädt die Akte neu.
  const inlineSpeichern = (e: MenschEntry, typ: FirmenKommunikationTyp, wert: string) => {
    if (!sofort || !firmaId || !e.ref?.contactId) return;
    if (sofort.hatUngespeicherte()) {
      setHinweis(UNGESPEICHERT_HINWEIS);
      return;
    }
    const k = e.ref.kommunikation;
    firmenKommunikationSpeichern({
      personId: e.ref.contactId,
      firmaId,
      typ,
      wert,
      vorhandenId: typ === "phone" ? k?.telefonId : k?.emailId,
    })
      .then(() => {
        setHinweis(null);
        sofort.neuLaden();
      })
      .catch((err) => setHinweis(sofortFehlerText(err, typ === "phone" ? "Das Telefon" : "Die E-Mail")));
  };
  const inlineEintrag = inlinePerson != null ? value.find((e) => e.ref?.contactId === inlinePerson && sofortFuer(e)) : null;
  const inline: InlineBearbeitung | undefined = sofort
    ? { aktivId: inlineEintrag?.id ?? null, zentrale, speichern: inlineSpeichern }
    : undefined;
  const menschenSpalten = menschenZusatzSpalten(value, absicherung, inline);
  const dialogEintrag = datenDialog ? value.find((e) => e.id === datenDialog.eintragId) : undefined;

  // Während eine Zeile der Firmenakte gezogen wird, stehen auch leere und
  // zugeklappte Listen als Ablage da (A6).
  const zieht = useZiehtGerade(ziehen ? ZIEH_ART_FIRMENAKTE : null);
  const tabellenZiehen = (liste: FirmenListe) =>
    ziehen ? { ziehArt: ZIEH_ART_FIRMENAKTE, ablage: ziehen.ablage(liste) } : {};
  const LEER_ABLAGE = "Hierher ziehen";

  const knoepfe = (
    <div className={s.fuss}>
      <HinzufuegenKnopf label={verantTitel} onClick={() => neu()} />
      <HinzufuegenKnopf label={menschTitel} onClick={() => neu(true)} />
    </div>
  );

  return (
    <div className={s.wrap}>
      {(leitungGesellschafter.length > 0 || zieht) && (
        <BeziehungsTabelle
          titel={`${leitungTitel(begriff)} und Gesellschafter`}
          spalten={spalten}
          zeilen={leitungGesellschafter}
          leerText={LEER_ABLAGE}
          {...tabellenZiehen("leitung")}
        />
      )}
      {(verantwortung.length > 0 || zieht) && (
        <BeziehungsTabelle
          titel={verantTitel}
          untertitel="Alle übrigen Verantwortlichkeiten und Zuständigkeiten"
          spalten={spaltenOhneAnteile}
          zeilen={verantwortung}
          leerText={LEER_ABLAGE}
          {...tabellenZiehen("verantwortung")}
        />
      )}
      {(menschen.length > 0 || zieht) && (
        <div className={s.klapp}>
          {/* Kein <button>: Aufklappen soll auch in der gesperrten Akte gehen. */}
          <AnsichtsKnopf
            label={`${menschTitel} (${menschen.length}) ${menschOffen ? "zuklappen" : "aufklappen"}`}
            className={s.klappKopf}
            onClick={() => setMenschOffen((o) => !o)}
          >
            <svg
              className={mergeClasses(s.chevron, menschOffen && s.chevronOffen)}
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 4l4 4-4 4" />
            </svg>
            <span>{menschTitel}</span>
            <span className={s.anzahl}>({menschen.length})</span>
          </AnsichtsKnopf>
          {hinweis && (
            <div role="alert" className={s.hinweis}>
              {hinweis}
            </div>
          )}
          {(menschOffen || zieht) && (
            <BeziehungsTabelle
              spalten={{ rolle: "Rolle", kontakt: false, status: false }}
              zusatzSpalten={menschenSpalten}
              zeilen={menschen}
              leerText={LEER_ABLAGE}
              {...tabellenZiehen("mensch")}
            />
          )}
        </div>
      )}
      {knoepfe}

      {datenDialog && dialogEintrag && sofort && firmaId && (
        <VerantwortungDatenDialog
          titel={verantTitel}
          eintrag={dialogEintrag}
          firmaId={firmaId}
          legalFormId={legalFormId}
          zentrale={zentrale}
          abs={absicherung}
          sofort={sofort}
          startModus={datenDialog.modus}
          onSchliessen={() => setDatenDialog(null)}
        />
      )}

      {offen && (
        <EintragDialog
          titel={verantTitel}
          kicker={offen.id === null ? "Hinzufügen" : "Bearbeiten"}
          nurAnsehen={offen.nurAnsehen}
          uebernehmenGesperrt={!offen.entwurf.ref}
          onUebernehmen={uebernehmen}
          onAbbrechen={abbrechen}
        >
          <div className={rs.refField}>
            <span className={rs.sectionLabel}>{offen.nurFirma ? "Firma" : "Person oder Firma"}</span>
            <ContactRefField
              wantType={offen.nurFirma ? "Firma" : "Person"}
              allowTypeSwitch={!offen.nurFirma}
              firmaExtraFields
              idBase={`mensch-${offen.entwurf.id}-p`}
              zentrale={zentrale}
              value={offen.entwurf.ref}
              onChange={(ref: ContactRef | null) => update({ ref })}
            />
          </div>
          <VerantwortlichkeitInput
            idBase={`mensch-${offen.entwurf.id}-v`}
            value={offen.entwurf.verant}
            onChange={(v) => update({ verant: v })}
            legalFormId={legalFormId}
            gegenkontaktIstFirma={
              offen.entwurf.ref ? offen.entwurf.ref.type === "Firma" : !!offen.nurFirma
            }
            // A8: Bei einer NEUEN Verantwortung ist der Haken „Mensch im
            // Unternehmen" für interne Rollen vorbelegt (User 02.10.2026).
            neuerEintrag={offen.id === null}
          />
          {/* Letzte Zeile: Eintrittsdatum | ausgeschieden? — auch beim Anlegen
              (User 28.09.2026). */}
          <AusgeschiedenFelder
            idBase={`mensch-${offen.entwurf.id}-aus`}
            value={offen.entwurf}
            onChange={update}
          />
        </EintragDialog>
      )}
    </div>
  );
}

/** Rollenprofil (`rechtsformProfil`) zur Rechtsform-Id — aus dem gecachten Katalog. */
function useRollenProfil(legalFormId?: number): string | null {
  const [profil, setProfil] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!legalFormId) {
      setProfil(null);
      return;
    }
    let aktiv = true;
    loadLegalFormOptionsCached()
      .then((optionen) => {
        if (!aktiv) return;
        const lf = optionen.find((o) => o.id === legalFormId);
        setProfil(lf ? rechtsformProfil({ legalForm: lf.legalForm, kuerzel: lf.kuerzel }) : null);
      })
      .catch(() => {
        /* ohne Katalog wird nichts ausgeblendet — lieber zu viel als zu wenig zeigen */
      });
    return () => {
      aktiv = false;
    };
  }, [legalFormId]);
  return profil;
}
